import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { viewableFields } from '@/features/records/utils/fieldAccess'
import { useForm } from '@/hooks/useForm'
import { useRecordGroups } from '@/hooks/useRecordGroups'
import { useTags } from '@/hooks/useTags'
import type { DashboardWidget } from '@/types/api/dashboard'
import { DashboardWidgetType } from '@/types/enums'

interface DataSourceOption {
  value: string
  label: string
  scopable: boolean
  requiresRecordGroup?: boolean
  requiresFormField?: boolean
  requiresTags?: boolean
  // Define which widget types this data source is compatible with
  compatibleWith: DashboardWidgetType[]
}

const SERIES_WIDGETS = [
  DashboardWidgetType.BAR_CHART,
  DashboardWidgetType.LINE_CHART,
  DashboardWidgetType.STACKED_BAR,
  DashboardWidgetType.PIE_CHART,
  DashboardWidgetType.DONUT_CHART,
  DashboardWidgetType.STATUS_BADGE_GROUP,
]
const SUMMARY_WIDGETS = [
  DashboardWidgetType.NUMBER_CARD,
  DashboardWidgetType.NUMBER_CARD_GROUP,
]

const DATA_SOURCES: DataSourceOption[] = [
  { value: 'records.summary', label: 'Records — Summary', scopable: true, compatibleWith: SUMMARY_WIDGETS },
  { value: 'records.bySourceType', label: 'Records — By Source Type', scopable: true, compatibleWith: SERIES_WIDGETS },
  { value: 'records.daily', label: 'Records — Created Per Day', scopable: true, compatibleWith: SERIES_WIDGETS },
  { value: 'records.fieldDistribution', label: 'Records — Field Value Distribution', scopable: true, requiresRecordGroup: true, requiresFormField: true, compatibleWith: SERIES_WIDGETS },
  { value: 'activity.recent', label: 'Activity — Recent Feed', scopable: true, compatibleWith: [DashboardWidgetType.ACTIVITY_FEED] },
  { value: 'activity.byType', label: 'Activity — By Type', scopable: true, compatibleWith: SERIES_WIDGETS },
  { value: 'recordGroups.summary', label: 'Record Groups — Summary', scopable: false, compatibleWith: SUMMARY_WIDGETS },
  { value: 'recordGroups.list', label: 'Record Groups — List', scopable: false, compatibleWith: [DashboardWidgetType.TABLE] },
  { value: 'automations.summary', label: 'Automations — Summary', scopable: false, compatibleWith: SUMMARY_WIDGETS },
  { value: 'automations.byTrigger', label: 'Automations — By Trigger', scopable: false, compatibleWith: SERIES_WIDGETS },
  { value: 'automations.list', label: 'Automations — List', scopable: false, compatibleWith: [DashboardWidgetType.TABLE] },
  { value: 'tasks.summary', label: 'Tasks — Summary', scopable: false, compatibleWith: SUMMARY_WIDGETS },
  { value: 'tasks.list', label: 'Tasks — List', scopable: false, compatibleWith: [DashboardWidgetType.TABLE] },
  { value: 'users.list', label: 'Users — List', scopable: false, compatibleWith: [DashboardWidgetType.TABLE] },
  { value: 'subscription.usage', label: 'Subscription — Usage', scopable: false, compatibleWith: [DashboardWidgetType.PROGRESS_BAR] },
  { value: 'users.summary', label: 'Users — Summary', scopable: false, compatibleWith: SUMMARY_WIDGETS },
  { value: 'records.list', label: 'Records — List', scopable: true, compatibleWith: [DashboardWidgetType.TABLE] },
  { value: 'records.byTag', label: 'Records — By Tag', scopable: true, requiresRecordGroup: true, requiresTags: true, compatibleWith: [DashboardWidgetType.TABLE] },
  { value: 'records.tagBreakdown', label: 'Records — Tag Breakdown', scopable: true, requiresRecordGroup: true, compatibleWith: SERIES_WIDGETS },
]

interface DashboardWidgetSettingsDialogProps {
  open: boolean
  widget: DashboardWidget | null
  lockedRecordGroupId?: string
  isNewWidget?: boolean // true if opened automatically right after drag-drop
  onOpenChange: (open: boolean) => void
  onSave: (updates: Partial<DashboardWidget>) => void
}

export function DashboardWidgetSettingsDialog({
  open,
  widget,
  lockedRecordGroupId,
  isNewWidget = false,
  onOpenChange,
  onSave,
}: DashboardWidgetSettingsDialogProps) {
  const { recordGroups } = useRecordGroups()
  const [title, setTitle] = useState('')
  const [dataSource, setDataSource] = useState('')
  const [recordGroupIds, setRecordGroupIds] = useState<string[]>([])
  const [formFieldId, setFormFieldId] = useState<string>('')
  const [tagIds, setTagIds] = useState<string[]>([])

  // The form hook uses the first selected record group (if any) to load the form schema for field picking
  const effectiveRecordGroupId = lockedRecordGroupId ?? (recordGroupIds.length > 0 ? recordGroupIds[0] : '')
  const { form } = useForm(effectiveRecordGroupId || '')
  const { tags } = useTags(effectiveRecordGroupId || undefined, 'RECORD')

  useEffect(() => {
    if (open && widget) {
      setTitle(widget.title)
      
      // Select an appropriate default data source if the widget doesn't have a valid one for its type.
      // A record group's dashboard can only show that group's data (the server rejects the rest).
      const compatibleSources = DATA_SOURCES.filter((ds) => ds.compatibleWith.includes(widget.type)
        && (!lockedRecordGroupId || ds.scopable))
      const isValidCurrentSource = compatibleSources.some((ds) => ds.value === widget.dataSource)
      
      if (isValidCurrentSource) {
        setDataSource(widget.dataSource)
      } else if (compatibleSources.length > 0) {
        setDataSource(compatibleSources[0].value)
      } else {
        setDataSource('')
      }

      const config = widget.dataSourceConfig as Record<string, unknown> | undefined
      
      // Handle legacy string recordGroupId and new array recordGroupIds
      let initialGroups: string[] = []
      if (Array.isArray(config?.recordGroupIds)) {
        initialGroups = config?.recordGroupIds as string[]
      } else if (typeof config?.recordGroupId === 'string') {
        initialGroups = [config.recordGroupId]
      }
      setRecordGroupIds(initialGroups)
      
      setFormFieldId(typeof config?.formFieldId === 'string' ? config.formFieldId : '')
      setTagIds(Array.isArray(config?.tagIds) ? (config.tagIds as string[]) : [])
    }
  }, [open, widget, lockedRecordGroupId])

  if (!widget) return null

  const selectedOption = DATA_SOURCES.find((option) => option.value === dataSource)
  // A record group's dashboard can only show that group's data (the server rejects the rest).
  const compatibleSources = DATA_SOURCES.filter((ds) => ds.compatibleWith.includes(widget.type)
    && (!lockedRecordGroupId || ds.scopable))

  const handleSave = () => {
    const config: Record<string, unknown> = { ...widget.dataSourceConfig }
    
    // Clear old single ID field if present
    delete config.recordGroupId
    
    if (selectedOption?.scopable) {
      if (lockedRecordGroupId) {
        config.recordGroupIds = [lockedRecordGroupId]
      } else if (recordGroupIds.length > 0) {
        config.recordGroupIds = recordGroupIds
      } else {
        delete config.recordGroupIds
      }
    } else {
      delete config.recordGroupIds
    }

    if (selectedOption?.requiresFormField && formFieldId) {
      config.formFieldId = formFieldId
    } else {
      delete config.formFieldId
    }

    if (selectedOption?.requiresTags && tagIds.length > 0) {
      config.tagIds = tagIds
    } else {
      delete config.tagIds
    }
    onSave({ title: title.trim() || widget.title, dataSource, dataSourceConfig: config })
    onOpenChange(false)
  }

  const toggleRecordGroup = (id: string) => {
    setRecordGroupIds((prev) => 
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    )
  }

  const toggleTag = (id: string) => {
    setTagIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    )
  }

  const canSave = title.trim().length > 0
    && dataSource !== ''
    && (!selectedOption?.requiresRecordGroup || (lockedRecordGroupId || recordGroupIds.length > 0))
    && (!selectedOption?.requiresFormField || !!formFieldId)
    && (!selectedOption?.requiresTags || tagIds.length > 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 shadow-sm">
              <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" /></svg>
            </div>
            <DialogTitle className="bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">{isNewWidget ? 'Configure Widget' : 'Widget Settings'}</DialogTitle>
          </div>
          <DialogDescription>
            {isNewWidget 
              ? 'Please configure your new widget before it is added to the dashboard.'
              : 'Choose what data this widget shows and its scope.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="widget-title">Title</Label>
            <Input id="widget-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="widget-data-source">Data source</Label>
            <Select value={dataSource} onValueChange={setDataSource}>
              <SelectTrigger id="widget-data-source">
                <SelectValue placeholder="Select a data source" />
              </SelectTrigger>
              <SelectContent>
                {compatibleSources.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
                {compatibleSources.length === 0 && (
                  <SelectItem value="" disabled>No compatible sources</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {selectedOption?.scopable && !lockedRecordGroupId && (
            <div className="space-y-1.5">
              <Label>Data scope</Label>
              <div className="max-h-40 overflow-y-auto rounded-md border border-neutral-200 p-2 space-y-1 scrollbar-thin">
                {!selectedOption.requiresRecordGroup && (
                  <label className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-neutral-50 ${recordGroupIds.length === 0 ? 'bg-primary-50' : ''}`}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded accent-primary-500"
                      checked={recordGroupIds.length === 0}
                      onChange={() => setRecordGroupIds([])}
                    />
                    <span className="text-neutral-700">Whole workspace (All record groups)</span>
                  </label>
                )}
                
                {recordGroups.map((group) => (
                  <label key={group.id} className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-neutral-50 ${recordGroupIds.includes(group.id) ? 'bg-primary-50' : ''}`}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded accent-primary-500"
                      checked={recordGroupIds.includes(group.id)}
                      onChange={() => toggleRecordGroup(group.id)}
                    />
                    <span className="text-neutral-700">{group.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {selectedOption?.requiresFormField && (
            <div className="space-y-1.5">
              <Label htmlFor="widget-field">Form field</Label>
              <Select value={formFieldId} onValueChange={setFormFieldId} disabled={!effectiveRecordGroupId || !form}>
                <SelectTrigger id="widget-field">
                  <SelectValue placeholder={effectiveRecordGroupId ? 'Select a field' : 'Select a record group first'} />
                </SelectTrigger>
                <SelectContent>
                  {viewableFields(form?.fields ?? []).map((field) => (
                    <SelectItem key={field.id} value={field.id}>{field.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {effectiveRecordGroupId && !form && (
                <p className="text-xs text-neutral-500">Loading form fields...</p>
              )}
            </div>
          )}

          {selectedOption?.requiresTags && (
            <div className="space-y-1.5">
              <Label>Tags (matches any selected)</Label>
              <div className="max-h-40 overflow-y-auto rounded-md border border-neutral-200 p-2 space-y-1 scrollbar-thin">
                {tags.map((tag) => (
                  <label key={tag.id} className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-neutral-50 ${tagIds.includes(tag.id) ? 'bg-primary-50' : ''}`}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded accent-primary-500"
                      checked={tagIds.includes(tag.id)}
                      onChange={() => toggleTag(tag.id)}
                    />
                    <span className="inline-flex h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tag.colorHex ?? '#a3a3a3' }} />
                    <span className="text-neutral-700">{tag.label}</span>
                  </label>
                ))}
                {tags.length === 0 && (
                  <p className="px-2 py-1.5 text-xs text-neutral-500">
                    {effectiveRecordGroupId ? 'No tags in this record group yet' : 'Select a record group first'}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave} className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-sm hover:from-violet-700 hover:to-indigo-700">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
